import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import Spinner from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'warning' | 'success' | 'ghost'
export type ButtonSize = 'sm' | 'md'

const BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700',
  secondary: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
  danger: 'bg-red-600 text-white hover:bg-red-700',
  // For actions that are disruptive but not destructive — restarting an
  // instance, writing over a filestore. Three pages had hand-rolled amber
  // buttons and had to fall back to primary without this.
  warning: 'bg-amber-600 text-white hover:bg-amber-700',
  success: 'bg-green-600 text-white hover:bg-green-700',
  ghost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
}

/** Both sizes stay on text-sm — that is what the app already used everywhere. */
const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Shows a spinner and disables the button. */
  loading?: boolean
  fullWidth?: boolean
  /** Rendered before the label; hidden while `loading`. */
  icon?: ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, fullWidth, icon, className, disabled, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // Buttons inside forms default to submit, which has bitten us before.
      type={type ?? 'button'}
      disabled={disabled || loading}
      className={cx(BASE, VARIANT[variant], SIZE[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  )
})

export default Button

/**
 * Text-only action, the shape table rows use ("Manage", "Edit", "Delete").
 * Not a `Button` variant because it carries no padding or background — mixing
 * it in would make every `size` prop a lie.
 */
export type LinkTone = 'accent' | 'danger' | 'success' | 'neutral'

const LINK_TONE: Record<LinkTone, string> = {
  accent: 'text-brand-600 hover:text-brand-700',
  danger: 'text-red-600 hover:text-red-800',
  success: 'text-green-600 hover:text-green-700',
  neutral: 'text-gray-600 hover:text-gray-900',
}

export function LinkButton({
  tone = 'accent',
  className,
  type,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: LinkTone }) {
  return (
    <button
      type={type ?? 'button'}
      className={cx(
        'text-xs font-medium transition-colors rounded-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        LINK_TONE[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
